import { useEffect, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap'
import { t } from '../../locale'
import { UidCheckModalProps, UidCompany } from '../../types/uid.type'
import { uidGetByClientId, uidGetByUid } from '../../service/uid'
import { computeUidCheckVariant } from '../../helpers/uidCheckVariant'

interface UidLocalData {
  name: string
  legalForm: string
  city: string
  canton: string
  status: string
}

type MatchResult = 'match' | 'mismatch' | 'unknown'

function normalise(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase()
}

function compareValues(local: string, uid: string | null | undefined): MatchResult {
  if (!local.trim() || !(uid ?? '').trim()) return 'unknown'
  return normalise(local) === normalise(uid) ? 'match' : 'mismatch'
}

function uidPublicStatusToLocal(status: number | null | undefined): string {
  return status === 1 ? 'active' : 'inactive'
}

function CompareRow({
  label,
  localVal,
  uidVal,
  result,
}: {
  label: string
  localVal: string | null | undefined
  uidVal: string | null | undefined
  result: MatchResult
}) {
  return (
    <tr>
      <th className='text-muted'>{label}</th>
      <td>{localVal || '-'}</td>
      <td>{uidVal || '-'}</td>
      <td className='text-center fw-bold'>
        {result === 'match' && <span className='text-success'>✓</span>}
        {result === 'mismatch' && <span className='text-danger'>✗</span>}
        {result === 'unknown' && <span className='text-muted'>—</span>}
      </td>
    </tr>
  )
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function UidCheckModal({ show, clientId, client, onClose, onCheckResult }: UidCheckModalProps) {
  const [allChecks, setAllChecks] = useState<UidCompany[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [data, setData] = useState<UidCompany | null>(null)
  const [loading, setLoading] = useState(false)
  const [uidInput, setUidInput] = useState('')
  const [inputMode, setInputMode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [legalForms, setLegalForms] = useState<{ value: string; label: string }[]>([])
  const [localData, setLocalData] = useState<UidLocalData>({
    name: '',
    legalForm: '',
    city: '',
    canton: '',
    status: 'active',
  })

  useEffect(() => {
    if (!show || !clientId) return
    loadExisting()
  }, [show, clientId])

  useEffect(() => {
    if (!client) return
    setLocalData({
      name: client.name ?? '',
      legalForm: client.stammdaten?.legal_form ?? '',
      city: client.stammdaten?.address?.[0]?.city ?? '',
      canton: '',
      status: client.status === 'inactive' ? 'inactive' : 'active',
    })
  }, [client])

  const selectCheck = (index: number, checks: UidCompany[]) => {
    const check = checks[index]
    setSelectedIndex(index)
    setData(check)
    if (check.localSnapshot) {
      setLocalData(check.localSnapshot)
    }
  }

  const loadExisting = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await uidGetByClientId(clientId!, true)
      if (Array.isArray(result) && result.length > 0) {
        setAllChecks(result)
        selectCheck(0, result)
        setInputMode(false)
      } else {
        setAllChecks([])
        setData(null)
        setInputMode(true)
      }
    } catch {
      setInputMode(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!uidInput.trim() || !clientId) return
    try {
      setLoading(true)
      setError(null)
      const result = await uidGetByUid(uidInput.trim(), clientId, localData)
      if (result?.uid) {
        const updated = [result, ...allChecks]
        setAllChecks(updated)
        selectCheck(0, updated)
        setInputMode(false)
      } else {
        setError(t('uidNotFound'))
      }
    } catch {
      setError(t('uidNotFound'))
    } finally {
      setLoading(false)
    }
  }

  const handleLocalChange =
    (field: keyof UidLocalData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setLocalData(prev => ({ ...prev, [field]: e.target.value }))

  const handleClose = () => {
    setAllChecks([])
    setSelectedIndex(0)
    setData(null)
    setUidInput('')
    setInputMode(false)
    setError(null)
    onClose()
  }

  const legalFormLabel = (code: string | null | undefined) =>
    legalForms.find(f => f.value === code)?.label ?? code ?? '-'

  const uidStatus = uidPublicStatusToLocal(data?.uidregInformation?.uidregPublicStatus)
  const nameMatch = compareValues(
    localData.name,
    data?.organisationName ?? data?.organisationLegalName
  )
  const legalFormMatch = compareValues(localData.legalForm, data?.legalForm)
  const cityMatch = compareValues(localData.city, data?.address?.town)
  const cantonMatch = compareValues(localData.canton, data?.address?.cantonAbbreviation)
  const statusMatch = compareValues(localData.status, uidStatus)
  const allResults = [nameMatch, legalFormMatch, cityMatch, cantonMatch, statusMatch]
  const mismatchCount = allResults.filter(r => r === 'mismatch').length
  const hasAnyComparison = allResults.some(r => r !== 'unknown')

  useEffect(() => {
    if (!data || !client || !hasAnyComparison) return
    onCheckResult?.(computeUidCheckVariant(client, data))
  }, [data, localData])

  return (
    <Modal show={show} centered size='lg' onHide={handleClose}>
      <Modal.Header className='bg-secondary text-white' closeButton>
        <Modal.Title className='fs-5'>{t('uidCheckTitle')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className='d-flex justify-content-center py-4'>
            <Spinner animation='border' variant='primary' />
          </div>
        )}

        {!loading && inputMode && (
          <>
            <h6 className='fw-semibold mb-2'>{t('uidLocalDataTitle')}</h6>
            <p className='text-muted small mb-3'>{t('uidLocalDataHint')}</p>

            <Table size='sm' bordered responsive className='mb-4'>
              <tbody>
                <tr>
                  <th className='text-muted w-35'>{t('uidLocalName')}</th>
                  <td>
                    <Form.Control
                      size='sm'
                      type='text'
                      value={localData.name}
                      onChange={handleLocalChange('name')}
                      placeholder={t('uidLocalNamePlaceholder')}
                    />
                  </td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLocalLegalForm')}</th>
                  <td>
                    <Form.Control
                      size='sm'
                      type='text'
                      value={localData.legalForm}
                      onChange={handleLocalChange('legalForm')}
                      placeholder={t('uidLocalLegalForm')}
                    />
                  </td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLocalCity')}</th>
                  <td>
                    <Form.Control
                      size='sm'
                      type='text'
                      value={localData.city}
                      onChange={handleLocalChange('city')}
                      placeholder={t('uidLocalCityPlaceholder')}
                    />
                  </td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLocalCanton')}</th>
                  <td>
                    <Form.Control
                      size='sm'
                      type='text'
                      value={localData.canton}
                      onChange={handleLocalChange('canton')}
                      placeholder='ZH, SZ, BE ...'
                      maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLocalStatus')}</th>
                  <td>
                    <Form.Select
                      size='sm'
                      value={localData.status}
                      onChange={handleLocalChange('status')}
                    >
                      <option value='active'>{t('active')}</option>
                      <option value='inactive'>{t('inactive')}</option>
                    </Form.Select>
                  </td>
                </tr>
              </tbody>
            </Table>

            <Form.Label className='fw-semibold'>{t('uidInputLabel')}</Form.Label>
            <div className='d-flex gap-2 mb-2'>
              <Form.Control
                type='text'
                placeholder={
                  window.location.hostname.endsWith('.ch') ? 'CHE-123.456.789' : 'DE123456789'
                }
                value={uidInput}
                onChange={e => setUidInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
              <Button variant='primary' onClick={handleSearch} disabled={!uidInput.trim()}>
                {t('uidSearch')}
              </Button>
            </div>
            {error && <div className='text-danger mt-2 small'>{error}</div>}
          </>
        )}

        {!loading && data && (
          <div>
            {allChecks.length > 1 && (
              <div className='mb-3'>
                <Form.Label className='fw-semibold small text-muted'>
                  {t('uidSelectCheck')}
                </Form.Label>
                <div className='d-flex flex-wrap gap-2'>
                  {allChecks.map((check, i) => (
                    <Button
                      key={check._id ?? i}
                      size='sm'
                      variant={i === selectedIndex ? 'primary' : 'outline-secondary'}
                      onClick={() => selectCheck(i, allChecks)}
                    >
                      {check.uid} &mdash; {formatDate(check.checkedAt)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h5 className='mb-0 fw-bold'>{data.organisationName}</h5>
              <Button size='sm' variant='outline-secondary' onClick={() => setInputMode(true)}>
                {t('uidNewSearch')}
              </Button>
            </div>

            {/* Comparison summary banner */}
            {hasAnyComparison && (
              <Alert variant={mismatchCount === 0 ? 'success' : 'warning'} className='py-2 mb-3'>
                {mismatchCount === 0 ? t('uidCompareAllMatch') : t('uidCompareSomeMismatch')}
              </Alert>
            )}

            {/* Comparison table */}
            {hasAnyComparison && (
              <>
                <h6 className='fw-semibold mb-2'>{t('uidCompareTitle')}</h6>
                <Table size='sm' bordered responsive className='mb-4'>
                  <thead>
                    <tr>
                      <th className='text-muted w-35'>{t('uidCompareField')}</th>
                      <th className='text-muted'>{t('uidCompareLocal')}</th>
                      <th className='text-muted'>{t('uidCompareUid')}</th>
                      <th className='text-muted text-center' style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    <CompareRow
                      label={t('uidLocalName')}
                      localVal={localData.name}
                      uidVal={data.organisationName}
                      result={nameMatch}
                    />
                    <CompareRow
                      label={t('uidLocalLegalForm')}
                      localVal={legalFormLabel(localData.legalForm)}
                      uidVal={legalFormLabel(data.legalForm)}
                      result={legalFormMatch}
                    />
                    <CompareRow
                      label={t('uidLocalCity')}
                      localVal={localData.city}
                      uidVal={data.address?.town}
                      result={cityMatch}
                    />
                    <CompareRow
                      label={t('uidLocalCanton')}
                      localVal={localData.canton.toUpperCase()}
                      uidVal={data.address?.cantonAbbreviation}
                      result={cantonMatch}
                    />
                    <CompareRow
                      label={t('uidLocalStatus')}
                      localVal={t(localData.status)}
                      uidVal={t(uidStatus)}
                      result={statusMatch}
                    />
                  </tbody>
                </Table>
              </>
            )}

            {/* Full UID register data */}
            <h6 className='fw-semibold mb-2'>{t('uidRegDetails')}</h6>
            <Table size='sm' bordered responsive>
              <tbody>
                <tr>
                  <th className='text-muted w-35'>{t('uidUid')}</th>
                  <td>
                    <Badge bg='dark'>{data.uid}</Badge>
                  </td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLegalName')}</th>
                  <td>{data.organisationLegalName ?? '-'}</td>
                </tr>
                <tr>
                  <th className='text-muted'>{t('uidLegalForm')}</th>
                  <td>{legalFormLabel(data.legalForm)}</td>
                </tr>
                {data.otherIds?.map((id, i) => (
                  <tr key={i}>
                    <th className='text-muted'>{id.organisationIdCategory}</th>
                    <td>{id.organisationId}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data.address && (
              <>
                <h6 className='fw-semibold mt-3 mb-2'>{t('uidAddress')}</h6>
                <Table size='sm' bordered responsive>
                  <tbody>
                    <tr>
                      <th className='text-muted w-35'>{t('uidStreet')}</th>
                      <td>
                        {data.address.street} {data.address.houseNumber}
                      </td>
                    </tr>
                    <tr>
                      <th className='text-muted'>{t('uidTown')}</th>
                      <td>
                        {data.address.swissZipCode} {data.address.town}
                      </td>
                    </tr>
                    <tr>
                      <th className='text-muted'>{t('uidCanton')}</th>
                      <td>{data.address.cantonAbbreviation}</td>
                    </tr>
                    <tr>
                      <th className='text-muted'>{t('uidCountry')}</th>
                      <td>{data.address.countryIdISO2}</td>
                    </tr>
                    {data.address.dateOfLastCheck && (
                      <tr>
                        <th className='text-muted'>{t('uidLastCheck')}</th>
                        <td>{data.address.dateOfLastCheck}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {data.uidregInformation && (
              <>
                <h6 className='fw-semibold mt-3 mb-2'>{t('uidRegInfo')}</h6>
                <Table size='sm' bordered responsive>
                  <tbody>
                    <tr>
                      <th className='text-muted w-35'>{t('uidregStatus')}</th>
                      <td>{data.uidregInformation.uidregStatusEnterpriseDetail ?? '-'}</td>
                    </tr>
                    <tr>
                      <th className='text-muted'>{t('uidregPublicStatus')}</th>
                      <td>{data.uidregInformation.uidregPublicStatus ?? '-'}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            {data.commercialRegisterInformation && (
              <>
                <h6 className='fw-semibold mt-3 mb-2'>{t('uidCommercialReg')}</h6>
                <Table size='sm' bordered responsive>
                  <tbody>
                    <tr>
                      <th className='text-muted w-35'>{t('uidCommercialStatus')}</th>
                      <td>{data.commercialRegisterInformation.commercialRegisterStatus ?? '-'}</td>
                    </tr>
                    <tr>
                      <th className='text-muted'>{t('uidCommercialEntryStatus')}</th>
                      <td>
                        {data.commercialRegisterInformation.commercialRegisterEntryStatus ?? '-'}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            <div className='text-muted small mt-3'>
              {t('uidCheckedAt')}: {data.checkedAt ? formatDate(data.checkedAt) : '-'}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default UidCheckModal
